import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  LayoutDashboard,
  Package,
  Palette,
  Rocket,
  type LucideIcon,
} from 'lucide-react';
import { Card, PageHeader } from '@/components/ui';
import { useAdminProducts } from '@/hooks/useAdminProducts';
import { useAdminDecorationMethods } from '@/hooks/useAdminDecorationMethods';

interface DashboardFigureState {
  value: number | undefined;
  isPending: boolean;
  isError: boolean;
}

interface FigureProps extends DashboardFigureState {
  label: string;
}

interface DashboardTile {
  title: string;
  description: string;
  to: string;
  icon: LucideIcon;
  /** Optional figure shown on the tile, e.g. the number of products. */
  figure?: FigureProps;
}

/**
 * A figure on a tile. Never falls back to "0": while the query is pending a
 * placeholder block stands in, on error a dash — a zero would read as a
 * real count.
 */
function Figure({ label, value, isPending, isError }: FigureProps) {
  let content: ReactNode;
  if (isPending) {
    content = (
      <>
        <span
          className="inline-block h-7 w-12 animate-pulse rounded-chip bg-paper-3"
          aria-hidden="true"
        />
        <span className="sr-only">Loading</span>
      </>
    );
  } else if (isError || value === undefined) {
    content = <span aria-label={`${label}: unavailable`}>–</span>;
  } else {
    content = value.toLocaleString('en-US');
  }

  return (
    <div className="mt-auto pt-4">
      <p className="dc-eyebrow">{label}</p>
      <p className="mt-1 font-display text-[28px] font-bold leading-none tracking-[-0.02em] text-ink">
        {content}
      </p>
    </div>
  );
}

/**
 * Admin dashboard: one entry tile per admin area, with the two figures the
 * existing list queries already deliver. Deliberately narrow — further
 * metrics are follow-up work.
 */
export function AdminDashboardPage() {
  // A single product is enough: only the total from the pagination meta is
  // used, so the list stays as small as the API allows.
  const products = useAdminProducts({ limit: 1 });
  const methods = useAdminDecorationMethods();

  const tiles: DashboardTile[] = [
    {
      title: 'Products',
      description: 'Activate products, set print areas and mockup layers.',
      to: '/admin/products',
      icon: Package,
      figure: {
        label: 'Products',
        value: products.data?.meta.total,
        isPending: products.isPending,
        isError: products.isError,
      },
    },
    {
      title: 'Decoration methods',
      description: 'Base prices per method and sales platform.',
      to: '/admin/decoration-methods',
      icon: Palette,
      figure: {
        label: 'Decoration methods',
        value: methods.data?.length,
        isPending: methods.isPending,
        isError: methods.isError,
      },
    },
    {
      title: 'Onboarding',
      description: 'Guide a new customer from account to first product.',
      to: '/admin/onboarding',
      icon: Rocket,
    },
    {
      title: 'Seller area',
      description: 'The design overview as a seller sees it.',
      to: '/designs',
      icon: LayoutDashboard,
    },
  ];

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">
      <PageHeader
        eyebrow="Admin"
        title="Dashboard"
        description="Entry points into the admin area."
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <Card key={tile.to} padding="none" className="flex">
              <Link
                to={tile.to}
                className="group flex w-full flex-col gap-3 rounded-card p-6 transition-[box-shadow] duration-[120ms] hover:ring-2 hover:ring-inset hover:ring-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-control bg-primary-soft text-primary">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="flex items-center gap-2">
                  <span className="dc-h3 text-base text-ink">{tile.title}</span>
                  <ArrowRight
                    className="h-4 w-4 text-mute transition-transform duration-[120ms] group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </span>
                <span className="text-sm text-ink-2">{tile.description}</span>
                {tile.figure && <Figure {...tile.figure} />}
              </Link>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
